import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetProductVariantsResult } from '@whinerp/client';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { addDecimals } from '@whinerp/contracts/money';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-41 Product variants and grid entry.
 *
 * Attributes above, the variants they produced below, and - when there are
 * exactly two attributes - a matrix to order across. Three decisions shape it.
 *
 * **The count is known before anything is written.** Four attributes with five
 * values each is 625 rows, each needing a SKU and a price. The screen says how
 * many and asks again above a threshold, rather than generating and leaving
 * somebody to delete six hundred rows by hand.
 *
 * **A variant is deactivated, never deleted.** It may be on a quote a customer
 * is holding, and removing the row would leave that document pointing at
 * nothing.
 *
 * **The grid needs two attributes and says so when it has not got them.** A
 * matrix has one axis across and one down; three attributes have no such
 * arrangement, and drawing an empty grid would read as the variants having
 * disappeared.
 */

type Variants = GetProductVariantsResult;

interface AttributeDraft {
  name: string;
  values: string;
}

function VariantsScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const queryClient = useQueryClient();
  const params = useParams();
  const productId = params['id'] ?? '';

  const variants = useQuery<Variants, ApiError>({
    queryKey: ['product-variants', productId],
    queryFn: () => api.getProductVariants({ path: { productId } }),
    // The table stays on screen while a regeneration lands. Without this the
    // whole screen drops to its loading state and takes the attribute editor
    // with it, mid-edit (P-27).
    placeholderData: keepPreviousData,
  });

  const [draft, setDraft] = useState<AttributeDraft[] | null>(null);
  const [confirming, setConfirming] = useState(false);

  // The draft starts from what the server has, and only once: re-seeding it on
  // every refetch would wipe what somebody was typing.
  useEffect(() => {
    if (draft === null && variants.data) {
      setDraft(variants.data.attributes.map((a) => ({ name: a.name, values: a.values.join(', ') })));
    }
  }, [variants.data, draft]);

  const saveAttributes = useMutation({
    mutationFn: (attributes: AttributeDraft[]) =>
      api.saveProductAttributes({
        path: { productId },
        body: {
          attributes: attributes
            .filter((a) => a.name.trim() !== '')
            .map((a) => ({
              name: a.name.trim(),
              values: a.values.split(',').map((v) => v.trim()).filter((v) => v !== ''),
            })),
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['product-variants', productId] }),
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  const generate = useMutation({
    mutationFn: (confirmed: boolean) =>
      api.generateVariants({ path: { productId }, body: { confirmed } }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
      toast.show({
        tone: 'success',
        title: t('variants.generated', {
          values: {
            created: formatCount(result.created, format),
            skipped: formatCount(result.skipped, format),
          },
        }),
      });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  const setVariant = useMutation({
    mutationFn: (input: { variantId: string; active?: boolean; priceAdjustment?: string }) =>
      api.updateProductVariant({
        path: { productId, variantId: input.variantId },
        body: {
          ...(input.active !== undefined ? { active: input.active } : {}),
          ...(input.priceAdjustment !== undefined ? { priceAdjustment: input.priceAdjustment } : {}),
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['product-variants', productId] }),
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  if (variants.isError) {
    return <VariantsError error={variants.error} retry={() => void variants.refetch()} />;
  }
  if (!variants.data) return <VariantsLoading />;

  const data = variants.data;
  const rows = draft ?? [];

  return (
    <div data-component="ProductVariants" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <PageHeading>{data.productName}</PageHeading>
        <Num className="text-meta text-ink-meta">{data.sku}</Num>
        <Num className="ms-auto text-body text-ink">
          {formatMoney(data.listPrice, format, { currency: data.currency })}
        </Num>
      </header>

      <Panel data-region="attributes">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('variants.attributes')}</SectionLabel>

          <ul className="flex flex-col gap-2">
            {rows.map((row, index) => (
              <li key={index} className="flex flex-wrap items-end gap-2" data-attribute-row={index}>
                <label className="flex flex-col gap-0.5">
                  <span className="text-micro uppercase tracking-label text-ink-label">
                    {t('variants.attribute.name')}
                  </span>
                  <input
                    data-attribute-name={index}
                    aria-label={t('variants.attribute.name')}
                    value={row.name}
                    onChange={(event) =>
                      setDraft(rows.map((r, i) => (i === index ? { ...r, name: event.target.value } : r)))
                    }
                    className="h-input w-panel-narrow rounded-control border border-control bg-surface px-2 text-body text-ink"
                  />
                </label>
                <label className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-micro uppercase tracking-label text-ink-label">
                    {t('variants.attribute.values')}
                  </span>
                  <input
                    data-attribute-values={index}
                    aria-label={t('variants.attribute.values')}
                    value={row.values}
                    onChange={(event) =>
                      setDraft(rows.map((r, i) => (i === index ? { ...r, values: event.target.value } : r)))
                    }
                    className="h-input w-full rounded-control border border-control bg-surface px-2 text-body text-ink"
                  />
                </label>
                <Button
                  intent="ghost"
                  data-remove-attribute={index}
                  onClick={() => setDraft(rows.filter((_, i) => i !== index))}
                >
                  {t('variants.attribute.remove', { values: { name: row.name || String(index + 1) } })}
                </Button>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              intent="secondary"
              data-add-attribute
              onClick={() => setDraft([...rows, { name: '', values: '' }])}
            >
              {t('variants.attribute.add')}
            </Button>
            <Button
              intent="primary"
              data-save-attributes
              disabled={saveAttributes.isPending}
              onClick={() => saveAttributes.mutate(rows)}
            >
              {t('variants.save')}
            </Button>

            {/* What generating would do, before it does it. Four attributes
                with five values each is 625 rows; the count belongs in front
                of the button, not in a toast afterwards. */}
            <span className="ms-auto flex items-center gap-3">
              <Num data-plan-total className="text-meta text-ink-meta">
                {t('variants.generate.count', {
                  values: { count: formatCount(data.plan.total, format) },
                })}
              </Num>
              {data.plan.existing > 0 ? (
                <Num data-plan-existing className="text-meta text-ink-meta">
                  {t('variants.generate.existing', {
                    values: { count: formatCount(data.plan.existing, format) },
                  })}
                </Num>
              ) : null}
              <Button
                intent="secondary"
                data-generate
                disabled={data.plan.total === 0 || generate.isPending}
                onClick={() => (data.plan.warn ? setConfirming(true) : generate.mutate(false))}
              >
                {t('variants.generate')}
              </Button>
            </span>
          </div>
        </div>
      </Panel>

      {data.variants.length === 0 ? (
        <VariantsEmpty reason={data.attributes.length === 0 ? 'no-records' : 'filtered-to-nothing'} />
      ) : (
        <Panel data-region="variant-table">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('variants.table')}</SectionLabel>
            <div className="w-full overflow-x-auto" data-scroll-container>
              <table className="w-full min-w-panel-wide">
                <thead>
                  <tr className="border-b border-b-rule text-start">
                    <th className="py-1 text-start text-micro uppercase tracking-label text-ink-label">
                      {t('variants.sku')}
                    </th>
                    {data.attributes.map((attribute) => (
                      <th
                        key={attribute.attributeId}
                        className="py-1 text-start text-micro uppercase tracking-label text-ink-label"
                      >
                        {attribute.name}
                      </th>
                    ))}
                    <th className="py-1 text-end text-micro uppercase tracking-label text-ink-label">
                      {t('variants.adjustment')}
                    </th>
                    <th className="py-1 text-end text-micro uppercase tracking-label text-ink-label">
                      {t('variants.price')}
                    </th>
                    <th className="py-1 text-end text-micro uppercase tracking-label text-ink-label">
                      {t('variants.active')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.variants.map((variant) => (
                    <tr key={variant.variantId} data-variant={variant.variantId} className="border-t border-t-rule">
                      <td className="py-1.5 text-body text-ink">{variant.sku}</td>
                      {data.attributes.map((attribute) => (
                        <td key={attribute.attributeId} className="py-1.5 text-body text-ink">
                          {variant.attributes[attribute.name] ?? ''}
                        </td>
                      ))}
                      <td className="py-1.5 text-end">
                        <input
                          data-variant-adjustment={variant.variantId}
                          aria-label={`${t('variants.adjustment')}: ${variant.sku}`}
                          defaultValue={variant.priceAdjustment}
                          onBlur={(event) => {
                            if (event.target.value === variant.priceAdjustment) return;
                            setVariant.mutate({
                              variantId: variant.variantId,
                              priceAdjustment: event.target.value,
                            });
                          }}
                          className="h-input w-block-sm rounded-control border border-control bg-surface px-2 text-end text-body text-ink"
                        />
                      </td>
                      {/* The price a customer would see: the list price plus
                          this variant's adjustment, added as decimals rather
                          than floats, so the table and the quote agree. */}
                      <td className="py-1.5 text-end">
                        <Num align="end" data-variant-price={variant.variantId} className="text-body text-ink">
                          {formatMoney(addDecimals(data.listPrice, variant.priceAdjustment), format, {
                            currency: data.currency,
                          })}
                        </Num>
                      </td>
                      <td className="py-1.5 text-end">
                        {/* A switch with its state in words beside it. A
                            toggle whose only state is its position is one a
                            person has to interpret. */}
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            data-variant-active={variant.variantId}
                            aria-label={`${t('variants.active')}: ${variant.sku}`}
                            checked={variant.active}
                            onChange={(event) =>
                              setVariant.mutate({
                                variantId: variant.variantId,
                                active: event.target.checked,
                              })
                            }
                          />
                          <span className="text-meta text-ink-meta">
                            {variant.active ? t('variants.active') : t('variants.inactive')}
                          </span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      )}

      <GridEntry grid={data.grid} />

      <ConfirmDialog
        open={confirming}
        title={t('variants.generate')}
        confirmLabel={t('variants.generate')}
        labels={{ cancel: t('action.cancel') }}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          generate.mutate(true);
        }}
      >
        {t('variants.generate.warn', { values: { count: formatCount(data.plan.total, format) } })}
      </ConfirmDialog>
    </div>
  );
}

/**
 * The matrix, when the product has exactly two attributes.
 *
 * Quantities are held here and totalled as they are typed, so somebody
 * ordering forty variants of one shirt sees the number they are committing to
 * before they commit to it.
 */
function GridEntry({ grid }: { grid: Variants['grid'] }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  if (!grid) {
    return (
      <Panel data-region="grid">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('variants.grid')}</SectionLabel>
          {/* Said, not left blank. An absent grid with no explanation reads as
              the variants having disappeared. */}
          <p data-grid-unavailable className="text-body text-ink-meta">
            {t('variants.grid.needsTwo')}
          </p>
        </div>
      </Panel>
    );
  }

  const entered = Object.values(quantities)
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  const units = entered.reduce((total, value) => total + value, 0);

  return (
    <Panel data-region="grid">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('variants.grid')}</SectionLabel>
        <div className="w-full overflow-x-auto" data-scroll-container>
          <table className="w-full min-w-panel-wide">
            <thead>
              <tr>
                <th className="py-1 text-start text-micro uppercase tracking-label text-ink-label">
                  {grid.rowAttribute}
                </th>
                {grid.columns.map((column) => (
                  <th
                    key={column}
                    className="py-1 text-end text-micro uppercase tracking-label text-ink-label"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr key={row} data-grid-row={row} className="border-t border-t-rule">
                  <th className="py-1.5 text-start text-body font-regular text-ink">{row}</th>
                  {grid.columns.map((column) => {
                    const cell = grid.cells[`${row}|${column}`];
                    return (
                      <td key={column} className="py-1.5 text-end">
                        {cell && cell.active ? (
                          <input
                            inputMode="numeric"
                            data-grid-cell={`${row}|${column}`}
                            aria-label={`${row} ${column} ${cell.sku}`}
                            value={quantities[`${row}|${column}`] ?? ''}
                            onChange={(event) =>
                              setQuantities((current) => ({
                                ...current,
                                [`${row}|${column}`]: event.target.value,
                              }))
                            }
                            className="h-input w-block-xs rounded-control border border-control bg-surface px-2 text-end text-body text-ink"
                          />
                        ) : (
                          /* A cell with no variant, or one switched off, says
                             so rather than offering a box that silently does
                             nothing when typed into. */
                          <span data-grid-missing={`${row}|${column}`} className="text-meta text-ink-meta">
                            {t('variants.grid.missing')}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Num data-grid-total className="text-dense text-ink">
          {t('variants.grid.total', {
            values: {
              count: formatCount(units, format),
              variants: formatCount(entered.length, format),
            },
          })}
        </Num>
      </div>
    </Panel>
  );
}

function VariantsEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="Cube"
      title={t('variants.empty.title')}
      body={t('variants.empty.body')}
      primary={{ label: t('empty.action.addVariant'), icon: 'Plus', href: '/setup' }}
    />
  );
}

function VariantsLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-block-sm w-full" />
      <Skeleton className="h-block-xl w-full" />
    </div>
  );
}

function VariantsError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="These variants could not be loaded"
      body={error instanceof Error ? error.message : 'The product did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-41',
  route: '/products/:id/variants',
  surface: 'page',
  title: 'Product variants & grid entry',
  features: ['F-160'],
  entitlement: 'F-160',
  reachedBy: [{ kind: 'setup', section: 'pricing' }],
  /*
   * No shared components from the library: 04_Screens names an attribute
   * editor, a variant table and a grid entry matrix, and all three are this
   * screen's own markup rather than anything reused. The empty state is
   * declared below as a state, which the check renders on its own - listing it
   * here as well would demand it appear on a screen that has variants.
   */
  components: [],
  // The confirmation exists only once somebody presses Generate above the
  // threshold, so it is declared as needing an interaction - which is the
  // stronger demand: a UI test has to drive the product to the point where it
  // appears, rather than the check merely finding it on a rendered screen.
  afterInteraction: ['ConfirmDialog'],
  elements: ['ProductVariants'],
  sampleRecord: { objectKey: 'product', param: 'id' },
  states: {
    empty: VariantsEmpty,
    loading: VariantsLoading,
    error: VariantsError,
  },
  component: VariantsScreen,
});

export default screen;
